/**
 * WMS DAOUXUN - Main Application
 * 
 * Core routing and provider hierarchy for the WMS SPA.
 * Role-based access: admin, manager, operator, client, team_leader, fba
 */
import React from 'react';
import ProductCockpitPage from './pages/admin/ProductCockpitPage';
import OrderCockpitPage from './pages/admin/OrderCockpitPage';
import InventoryCockpitPage from './pages/admin/InventoryCockpitPage';
import WorkforceCockpitPage from './pages/admin/WorkforceCockpitPage';
import ReturnsCockpitPage from './pages/admin/ReturnsCockpitPage';
import LogisticsCockpitPage from './pages/admin/LogisticsCockpitPage';
import FinancialCockpitPage from './pages/admin/FinancialCockpitPage';
import AICockpitPage from './pages/admin/AICockpitPage';
import WorkstationMonitoringPage from './pages/admin/WorkstationMonitoringPage';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SearchResultsPage from './pages/SearchResultsPage.jsx';
import './styles/animations.css';
// Error handling
import ErrorBoundary from './components/common/ErrorBoundary.jsx';
import ErrorBoundaryWithAPI from './components/common/ErrorBoundaryWithAPI.jsx';

import PageTransition from './components/ui/PageTransition.jsx';
import PremiumLoader from './components/ui/PremiumLoader.jsx';

// Contexts
import { AuthProvider } from './contexts/AuthContext.jsx';
import { ThemeModeProvider } from './contexts/ThemeContext.jsx';
import { useThemeMode } from './contexts/ThemeContext.helpers.js';
import { RoleProvider } from './contexts/RoleContext.jsx';
import AdvancedThemeProvider from './components/common/AdvancedThemeProvider.jsx';

import MainLayout from './layouts/MainLayout.jsx';
import ProtectedRoute from './components/auth/ProtectedRoute';

import LoginPageWithRoles from './pages/LoginPageWithRoles.jsx';
import UnauthorizedPage from './pages/UnauthorizedPage.jsx';

import SelfServiceRoute from './pages/self-service-fba/SelfServiceRoute';

const ConfigurationPage = React.lazy(() => import(/* webpackChunkName: "admin-config", webpackPrefetch: true */ './pages/admin/ConfigurationPage'));
const CustomerCockpitPage = React.lazy(() => import(/* webpackChunkName: "admin-customer-cockpit" */ './pages/admin/CustomerCockpitPage'));
const PickingVisualizationPage = React.lazy(() => import(/* webpackChunkName: "admin-picking-viz" */ './pages/admin/PickingVisualizationPage'));
const RoleManagementPage = React.lazy(() => import(/* webpackChunkName: "admin-roles" */ './pages/admin/RoleManagementPage'));
const SecurityDemoPage = React.lazy(() => import(/* webpackChunkName: "admin-security" */ './pages/admin/SecurityDemoPage'));
const OmnichannelDashboard= React.lazy(() => import(/* webpackChunkName: "admin-omnichannel" */ './pages/admin/OmnichannelDashboard'));

// Manager Pages
const ReturnsForecastDashboard= React.lazy(() => import(/* webpackChunkName: "manager-returns-forecast", webpackPrefetch: true */ './pages/manager/ReturnsForecastDashboard.jsx'));
const CycleCountManagementPage = React.lazy(() => import(/* webpackChunkName: "manager-cycle-count" */ './pages/manager/CycleCountManagementPage.jsx'));
const BackorderQueuePage = React.lazy(() => import(/* webpackChunkName: "manager-backorder" */ './pages/manager/BackorderQueuePage.jsx'));
const ManagerDashboard = React.lazy(() => import(/* webpackChunkName: "manager-dashboard", webpackPrefetch: true */ './pages/manager/ManagerDashboard.jsx'));
const DocumentSigningPage = React.lazy(() => import(/* webpackChunkName: "manager-document-signing" */ './pages/manager/documents/DocumentSigningPage.jsx'));
const InvoiceOversightPage = React.lazy(() => import(/* webpackChunkName: "manager-invoice-oversight" */ './pages/manager/InvoiceOversightPage.jsx'));

// Operator Pages
const NewOperatorPage = React.lazy(() => import(/* webpackChunkName: "operator-main", webpackPrefetch: true */ './pages/operator/NewOperatorPage.jsx'));
const InventoryPage = React.lazy(() => import(/* webpackChunkName: "operator-inventory", webpackPrefetch: true */ './pages/operator/InventoryPage.jsx'));
const PickingPage = React.lazy(() => import(/* webpackChunkName: "operator-picking", webpackPrefetch: true */ './pages/operator/PickingPage.jsx'));
const CycleCountingPage = React.lazy(() => import(/* webpackChunkName: "operator-cycle-count" */ './pages/operator/CycleCountingPage.jsx'));

// Team Leader Pages
const TeamLeaderPage = React.lazy(() => import('./pages/team-leader/TeamLeaderPage.jsx'));

// PDA Mobile Pages
const PDAMobilePage = React.lazy(() => import('./pages/pda/PDAMobilePage'));

// Workstation Pages
const WorkstationStartupPage = React.lazy(() => import(/* webpackChunkName: "workstation-startup", webpackPrefetch: true */ './pages/workstations/WorkstationStartupPage.jsx'));
const WorkstationLoginPage = React.lazy(() => import(/* webpackChunkName: "workstation-login", webpackPrefetch: true */ './pages/workstations/WorkstationLoginPage.jsx'));
const PackingStationDashboard = React.lazy(() => import(/* webpackChunkName: "workstation-packing-dash" */ './pages/workstations/PackingStationDashboardAdvanced.jsx'));
const ReceivingStationDashboard = React.lazy(() => import(/* webpackChunkName: "workstation-receiving-dash" */ './pages/workstations/ReceivingStationDashboard.jsx'));
const SecureReceivingStationDashboard = React.lazy(() => import(/* webpackChunkName: "workstation-secure-receiving" */ './pages/workstations/SecureReceivingStationDashboard.jsx'));
const ReturnsStationDashboard = React.lazy(() => import(/* webpackChunkName: "workstation-returns-dash" */ './pages/workstations/ReturnsStationDashboard.jsx'));
const BlindReturnsStationPage = React.lazy(() => import(/* webpackChunkName: "workstation-blind-returns", webpackPrefetch: true */ './pages/workstations/BlindReturnsStationPage.jsx'));
const InboundReceivingStationDashboard = React.lazy(() => import(/* webpackChunkName: "workstation-inbound" */ './pages/workstations/InboundReceivingStationDashboard.jsx'));
const QualityControlStationDashboard = React.lazy(() => import(/* webpackChunkName: "workstation-qc" */ './pages/workstations/QualityControlStationDashboard.jsx'));

// Self-Service FBA Pages
const SelfRegistrationPage = React.lazy(() => import('./pages/SelfRegistrationPage.jsx'));
const PublicLayout = React.lazy(() => import('./components/marketing/PublicLayout.jsx'));

const HomePage = React.lazy(() => import('./pages/public/HomePage.jsx'));
const PricingPage = React.lazy(() => import('./pages/public/PricingPage.jsx'));
const FeaturesPage = React.lazy(() => import('./pages/public/FeaturesPage.jsx'));
const ContactPage = React.lazy(() => import('./pages/public/ContactPage.jsx'));
const OnboardingPage = React.lazy(() => import('./pages/public/OnboardingPage.jsx'));
const ChatbotDemoPage = React.lazy(() => import('./pages/public/ChatbotDemoPage.jsx'));
const FrappeCRMComponentsDemo= React.lazy(() => import('./pages/demo/FrappeCRMComponentsDemo.jsx'));

// SpacetimeDB Monitoring Dashboard
const MonitoringDashboard = React.lazy(() => import('./dashboards/monitoring/MonitoringDashboard.jsx'));

// Utility Pattern Monitoring Dashboard (Fleetbase-inspired)
const UtilityMonitoringDashboard = React.lazy(() => import('./components/monitoring/UtilityMonitoringDashboard.jsx'));

// Loading fallback component
const LoadingFallback = ({ message = null }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    minHeight="400px"
    sx={{
      background: 'linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(255,255,255,0.05) 100%)',
      backdropFilter: 'blur(10px)',
      borderRadius: 2
    }}
  >
    <PremiumLoader
      type="holographic"
      message={message}
      size="large"
    />
  </Box>
);

const WorkstationLayout = () => (
  <React.Suspense fallback={<LoadingFallback />}>
    <Outlet />
  </React.Suspense>
);

const IntelligenceHubPage = React.lazy(() => import('./pages/admin/IntelligenceHubPage'));
const AIPage = AICockpitPage;
const AdminDashboard = React.lazy(() => import(/* webpackChunkName: "admin-dashboard", webpackPrefetch: true */ './pages/admin/AdminDashboard'));
const OperationsWMS = React.lazy(() => import('./components/manager/OperationsWMS'));
const Warehouse3DVisualization = React.lazy(() => import('./components/manager/Warehouse3DVisualization'));
const WarehouseLayoutEditor = React.lazy(() => import('./components/3d/WarehouseLayoutEditor'));
const WarehouseLayoutEditorPage = React.lazy(() => import('./pages/admin/WarehouseLayoutEditorPage'));
const Warehouse3D = React.lazy(() => import('./components/3d/Warehouse3D'));
const PermissionsPage = React.lazy(() => import('./pages/admin/PermissionsPage'));
const UserManagementAdvancedPage = WorkforceCockpitPage;
const EmployeesListPage = WorkforceCockpitPage;
const UserManagementPage = React.lazy(() => import('./components/business/UserManagement'));

const WarehousesListPage = React.lazy(() => import('./pages/admin/WarehousesListPage'));
const PickingOptimizationPage = React.lazy(() => import('./pages/admin/PickingOptimizationPage'));
const PickingWavesPage = React.lazy(() => import('./pages/admin/PickingWavesPage'));
const PickingListsPage = React.lazy(() => import('./pages/admin/PickingListsPage'));

const DeadlineControlAgentDashboard = React.lazy(() => import('./components/ai/control-agents/DeadlineControlAgentDashboard'));
const FlowControlAgentDashboard = React.lazy(() => import('./components/ai/control-agents/FlowControlAgentDashboard'));
const InventoryControlAgentDashboard = React.lazy(() => import('./components/ai/control-agents/InventoryControlAgentDashboard'));
const ErrorControlAgentDashboard = React.lazy(() => import('./components/ai/control-agents/ErrorControlAgentDashboard'));
const ControlAgentsDashboard = React.lazy(() => import('./components/ai/control-agents/ControlAgentsDashboard'));

const ChatbotPage = React.lazy(() => import('./pages/admin/ChatbotPage'));
const PerformancePage = React.lazy(() => import('./pages/admin/PerformancePage'));
const AdvancedAnalyticsPage = React.lazy(() => import('./pages/admin/AdvancedAnalyticsPage'));

const NotificationPage = React.lazy(() => import('./pages/admin/NotificationPage'));
const ReportingPage = React.lazy(() => import('./pages/admin/ReportingPage'));
const QualityControlPage = React.lazy(() => import('./pages/admin/QualityControlPage'));
const BillingEstimatePage = React.lazy(() => import('./pages/client/BillingEstimatePage'));
const ExecutiveDashboardPage = React.lazy(() => import('./pages/admin/ExecutiveDashboardPage'));
const CustomReportBuilderPage = React.lazy(() => import('./pages/admin/CustomReportBuilderPage'));
const ExchangeManagementPage= React.lazy(() => import('./pages/admin/ExchangeManagementPage'));
const MarketplaceConnectionsPage = React.lazy(() => import('./pages/admin/MarketplaceConnectionsPage'));

const WarehouseManagementPage = React.lazy(() => import('./pages/admin/WarehouseManagementPage'));
const PickingRoutesPage = React.lazy(() => import('./pages/admin/PickingRoutesPage'));
const DashboardWidgetsPage = React.lazy(() => import('./pages/admin/DashboardWidgetsPage'));
const UIEnhancerPage = React.lazy(() => import('./pages/admin/UIEnhancerPage'));
const LiveMonitoringPage = React.lazy(() => import('./pages/admin/LiveMonitoringPage'));
const EcosystemDashboardPage = React.lazy(() => import('./pages/admin/EcosystemDashboardPage'));
const MeshNetworkStatusPage = React.lazy(() => import('./pages/admin/MeshNetworkStatusPage'));

const ResourcePlanningPage = React.lazy(() => import('./pages/admin/ResourcePlanningPage'));
const MultiWarehousePage = React.lazy(() => import('./pages/admin/MultiWarehousePage'));
const ChatbotAssistantPage = React.lazy(() => import('./pages/admin/ChatbotAssistantPage'));
const PerformanceBoosterPage = React.lazy(() => import('./pages/admin/PerformanceBoosterPage'));
const UXEnhancerPage = React.lazy(() => import('./pages/admin/UXEnhancerPage'));
const PartnerManagementPage = React.lazy(() => import('./pages/admin/PartnerManagementPage'));
const LabelManagementPage = React.lazy(() => import('./pages/admin/LabelManagementPage'));
const BillingDashboardClientPage = React.lazy(() => import('./pages/client/BillingPageModern'));
const SupportPage = React.lazy(() => import('./pages/client/SupportPageModern'));
const OrdersPage = React.lazy(() => import('./pages/client/OrdersPage'));
const ClientDashboard = React.lazy(() => import(/* webpackChunkName: "client-dashboard", webpackPrefetch: true */ './pages/client/ClientDashboard'));
const OrderDetailPage = React.lazy(() => import('./pages/client/OrderDetailPage'));
const ProductOMSPage = React.lazy(() => import('./pages/client/ProductOMSPage'));
const OMSPage = React.lazy(() => import('./pages/client/OMSPage'));
const TMSShipmentPage = React.lazy(() => import('./pages/client/TMSShipmentPage'));
const TMSPage = React.lazy(() => import('./pages/client/TMSPage'));
const TTMSPage = React.lazy(() => import('./pages/client/TTMSPage'));
const ProfilePage = React.lazy(() => import('./pages/client/ProfilePage'));

// TMS Frontend Pages (Fleetbase-inspired TMS features)
const FleetManagementPage = React.lazy(() => import('./pages/tms/FleetManagementPage'));
const DriverDispatchPage = React.lazy(() => import('./pages/tms/DriverDispatchPage'));
const YardManagementPage = React.lazy(() => import('./pages/tms/YardManagementPage'));
const DockSchedulingPage = React.lazy(() => import('./pages/tms/DockSchedulingPage'));
const ClaimsManagementPage = React.lazy(() => import('./pages/tms/ClaimsManagementPage'));
const FreightBillingPage = React.lazy(() => import('./pages/tms/FreightBillingPage'));
const LoadPlanningPage = React.lazy(() => import('./pages/tms/LoadPlanningPage'));
const ReportsPage = React.lazy(() => import('./pages/client/ReportsPage'));
const CreateRMAPage = React.lazy(() => import('./pages/client/CreateRMAPage'));
const RMADetailsPage = React.lazy(() => import('./pages/client/RMADetailsPage'));
const InvoicePortalPage = React.lazy(() => import('./pages/client/InvoicePortalPage'));
const InvoiceDetailPortalPage = React.lazy(() => import('./pages/client/InvoiceDetailPortalPage'));
const CMSIntegrationsPage = React.lazy(() => import('./pages/client/CMSIntegrationsPage'));
const ParcelSortingPage = React.lazy(() => import('./pages/client/ParcelSortingPage'));
const ClientInventoryPage = React.lazy(() => import('./pages/client/InventoryPage'));

const WorkflowsPage = React.lazy(() => import('./components/workflows/WorkflowsPage'));

// Vibecraft Components - Vibecraft-inspired AI visualization features
const AIToolVisualizationDashboard = React.lazy(() => import(/* webpackChunkName: "vibecraft-tools" */ './components/vibecraft/AIToolVisualizationDashboard'));
const WorkstationOrchestrationView = React.lazy(() => import(/* webpackChunkName: "vibecraft-workstations" */ './components/vibecraft/WorkstationOrchestrationView'));
const ActivityFeedPanel = React.lazy(() => import(/* webpackChunkName: "vibecraft-activity" */ './components/vibecraft/ActivityFeedPanel'));
const AIAgentMonitor3D = React.lazy(() => import(/* webpackChunkName: "vibecraft-3d" */ './components/vibecraft/AIAgentMonitor3D'));

// Supermemory-inspired Memory Graph visualization
const MemoryGraph = React.lazy(() => import(/* webpackChunkName: "memory-graph" */ './components/ai/MemoryGraph'));

const TransportManagementPage= React.lazy(() => import('./pages/admin/TransportManagementPage'));
const ForecastingManagementPage = React.lazy(() => import('./pages/admin/ForecastingManagementPage'));
const DeliverySlotManagementPage = React.lazy(() => import('./pages/admin/DeliverySlotManagementPage'));
const AnalyticsAdvancedManagementPage = React.lazy(() => import('./pages/admin/AdvancedAnalyticsPage'));
const QualityControlManagementPage = React.lazy(() => import('./pages/admin/QualityControlManagementPage'));
const PlanningManagementPage = React.lazy(() => import('./pages/admin/PlanningManagementPage'));
const InventoryManagementPage = InventoryCockpitPage;

const ShippingManagementPage = React.lazy(() => import('./pages/admin/ShippingManagementPage'));

const PickingManagementPage = React.lazy(() => import('./pages/admin/PickingManagementPage'));
const AIActionsDashboardPage = AICockpitPage;
const SerialNumbersListPage = React.lazy(() => import(/* webpackChunkName: "admin-serial-numbers" */ './pages/admin/SerialNumbersListPage'));

const SettingsPage = React.lazy(() => import('./pages/admin/SettingsPage'));
const SystemSettingsPage = React.lazy(() => import('./pages/admin/SystemSettingsPage'));
const AuditLogsPage = React.lazy(() => import('./pages/admin/AuditLogsPage'));
const MarketplacePage = React.lazy(() => import('./pages/admin/MarketplacePage'));
const AdminInventoryPage = InventoryCockpitPage;

// ERPNext Gap Fixes Pages
const PutawayRulesPage = React.lazy(() => import('./pages/admin/PutawayRulesPage'));
const StockReservationsPage = React.lazy(() => import('./pages/admin/StockReservationsPage'));
const AutomaticReorderPage = React.lazy(() => import('./pages/admin/AutomaticReorderPage'));
const NonConformanceManagementPage = React.lazy(() => import('./pages/admin/NonConformanceManagementPage'));
const StockAgeingReportsPage = React.lazy(() => import('./pages/admin/StockAgeingReportsPage'));

const SupportManagementPage = React.lazy(() => import('./pages/admin/SupportManagementPage'));
const TMSOperationsPage = React.lazy(() => import('./pages/admin/TMSOperationsPage'));
const OrderOperationsPage = React.lazy(() => import('./pages/admin/OrderOperationsPage'));
const TeamOperationsPage = React.lazy(() => import('./pages/admin/TeamOperationsPage'));
const MonitoringOperationsPage = React.lazy(() => import('./pages/admin/MonitoringOperationsPage'));
const InventoryOperationsPage = React.lazy(() => import('./pages/admin/InventoryOperationsPage'));
const BillingOperationsPage = React.lazy(() => import('./pages/admin/BillingOperationsPage'));
const AnalyticsOperationsPage = React.lazy(() => import('./pages/admin/AnalyticsOperationsPage'));
const MarketplaceOperationsPage = React.lazy(() => import('./pages/admin/MarketplaceOperationsPage'));
const Warehouse3DToolsPage = React.lazy(() => import('./pages/admin/Warehouse3DToolsPage'));
const ProductOperationsPage = React.lazy(() => import('./pages/admin/ProductOperationsPage'));
const ManagerToolsPage = React.lazy(() => import('./pages/admin/ManagerToolsPage'));
const B2BOperationsPage = React.lazy(() => import('./pages/admin/B2BOperationsPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Main application component
const AppContent = () => {
  const { theme } = useThemeMode();


  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AdvancedThemeProvider>
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            autoHideDuration={6000}
          >
            <ErrorBoundaryWithAPI>
              <Router>
                <AuthProvider>
                  <RoleProvider>
                    <PageTransition>
                      <Routes>
                        {/* Public Marketing Routes */}
                        <Route path="/" element={
                          <React.Suspense fallback={<LoadingFallback message="Loading..." />}>
                            <PublicLayout />
                          </React.Suspense>
                        }>
                          <Route index element={
                            <React.Suspense fallback={<LoadingFallback message="Loading..." />}>
                              <HomePage />
                            </React.Suspense>
                          } />
                          <Route path="pricing" element={
                            <React.Suspense fallback={<LoadingFallback message="Loading..." />}>
                              <PricingPage />
                            </React.Suspense>
                          } />
                          <Route path="/features" element={<Navigate to="/features/wms" replace />} />
                          <Route path="/features/:featureType" element={
                            <React.Suspense fallback={<LoadingFallback message="Loading..." />}>
                              <FeaturesPage />
                            </React.Suspense>
                          } />
                          <Route path="/contact" element={
                            <React.Suspense fallback={<LoadingFallback message="Loading..." />}>
                              <ContactPage />
                            </React.Suspense>
                          } />
                          <Route path="/register" element={
                            <React.Suspense fallback={<LoadingFallback message="Loading..." />}>
                              <SelfRegistrationPage />
                            </React.Suspense>
                          } />
                          <Route path="/login" element={<LoginPageWithRoles />} />
                        </Route>

                        {/* Public Chatbot Route (No Auth Required) */}
                        <Route path="/public/chatbot" element={
                          <React.Suspense fallback={<LoadingFallback message="Loading chatbot..." />}>
                            <ChatbotDemoPage />
                          </React.Suspense>
                        } />

                        <Route path="/onboarding" element={
                          <ProtectedRoute roles={['client', 'admin']}>
                            <React.Suspense fallback={<LoadingFallback message="Loading..." />}>
                              <OnboardingPage />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />

                        {/* Auth routes */}
                        <Route path="/unauthorized" element={<UnauthorizedPage />} />
                        <Route path="/access-denied" element={<UnauthorizedPage />} />

                        {/* Explicit login redirects for role-specific URLs */}
                        <Route path="/manager/login" element={<Navigate to="/login" replace />} />
                        <Route path="/admin/login" element={<Navigate to="/login" replace />} />
                        <Route path="/operator/login" element={<Navigate to="/login" replace />} />

                        <Route path="/dashboard" element={<Navigate to="/client/dashboard" replace />} />

                        {/* Operator Routes */}
                        <Route path="/operator/*" element={
                          <ProtectedRoute roles={['operator', 'manager', 'admin']}>
                            <React.Suspense fallback={<LoadingFallback />}>
                              <Routes>
                                <Route index element={<NewOperatorPage />} />
                                <Route path="dashboard" element={<NewOperatorPage />} />
                                <Route path="picking" element={<NewOperatorPage />} />
                                <Route path="planning" element={<NewOperatorPage />} />
                                <Route path="tasks" element={<NewOperatorPage />} />
                                <Route path="warehouse_locations" element={<NewOperatorPage />} />
                                <Route path="returns" element={<NewOperatorPage />} />
                                <Route path="config" element={<NewOperatorPage />} />
                                                              <Route path="inventory_items" element={<InventoryPage />} />
                                                              <Route path="cycle-counting" element={<CycleCountingPage />} />
                                                              <Route path="tms" element={<TMSPage />} />
                                                            </Routes>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />

                        {/* Team Leader Routes */}
                        <Route path="/team-leader/*" element={
                          <ProtectedRoute roles={['team_leader', 'manager', 'admin']}>
                            <React.Suspense fallback={<LoadingFallback />}>
                              <Routes>
                                <Route index element={<TeamLeaderPage />} />
                                <Route path="dashboard" element={<TeamLeaderPage />} />
                              </Routes>
                            </React.Suspense>
                          </ProtectedRoute>
                        } />

                        {/* PDA Mobile Routes */}
                        <Route path="/pda" element={<PDAMobilePage />} />
                        <Route path="/pda/mobile" element={<PDAMobilePage />} />

                        {/* Workstation Routes */}
                        <Route path="/workstation" element={<WorkstationLayout />}>
                          <Route index element={<WorkstationStartupPage />} />
                          <Route path="startup" element={<WorkstationStartupPage />} />
                          <Route path="login" element={<WorkstationStartupPage />} />
                          <Route path="preparation" element={<PackingStationDashboard />} />
                          <Route path="receiving" element={<ReceivingStationDashboard />} />
                          <Route path="receiving-secure" element={<SecureReceivingStationDashboard />} />
                          <Route path="return" element={<ReturnsStationDashboard />} />
                          <Route path="blind-returns" element={<BlindReturnsStationPage />} />
                          <Route path="inbound-receiving" element={<InboundReceivingStationDashboard />} />
                          <Route path="quality-control" element={<QualityControlStationDashboard />} />
                          {/* Routes with station_code parameter */}
                          <Route path=":station_code/preparation" element={<PackingStationDashboard />} />
                          <Route path=":station_code/receiving" element={<ReceivingStationDashboard />} />
                          <Route path=":station_code/receiving-secure" element={<SecureReceivingStationDashboard />} />
                          <Route path=":station_code/return" element={<ReturnsStationDashboard />} />
                          <Route path=":station_code/blind-returns" element={<BlindReturnsStationPage />} />
                          <Route path=":station_code/inbound-receiving" element={<InboundReceivingStationDashboard />} />
                          <Route path=":station_code/quality-control" element={<QualityControlStationDashboard />} />
                        </Route>

                        {/* Manager Routes */}
                        <Route path="/manager/*" element={
                          <ProtectedRoute roles={['manager', 'admin']}>
                            <MainLayout>
                              <React.Suspense fallback={<LoadingFallback />}>
                                <Routes>
                                  <Route index element={<ManagerDashboard />} />
                                  <Route path="dashboard" element={<ManagerDashboard />} />
                                  <Route path="operations" element={<OperationsWMS />} />
                                  
                                                                    {/* Cockpits - Manager Access */}
                                                                    <Route path="orders" element={<OrderCockpitPage />} />
                                                                    <Route path="orders/:orderId" element={<OrderDetailPage />} />
                                                                    <Route path="products" element={<ProductCockpitPage />} />
                                  <Route path="inventory" element={<InventoryCockpitPage />} />
                                  <Route path="workforce" element={<WorkforceCockpitPage />} />
                                  <Route path="returns" element={<ReturnsCockpitPage />} />
                                  <Route path="logistics" element={<LogisticsCockpitPage />} />
                                  <Route path="analytics" element={<IntelligenceHubPage />} />

                                  {/* Specific Manager Pages */}
                                  <Route path="warehouse" element={<Warehouse3DVisualization />} />
                                                                    <Route path="warehouse-3d" element={<Warehouse3D />} />
                                                                                                    <Route path="returns-forecast"element={<ReturnsForecastDashboard />} />
                                                                  <Route path="cycle-count-management" element={<CycleCountManagementPage />} />
                                                                                                                                  <Route path="backorder-queue" element={<BackorderQueuePage />} />
                                                                                                                                  <Route path="tms" element={<TMSPage />} />
                                                                                                                                                                                                                    <Route path="ttms-trading" element={<TTMSPage />} />
                                                                                                                    <Route path="search" element={<SearchResultsPage />} />
                                                                                                                    <Route path="documents" element={<DocumentSigningPage />} />
                                                                                                                    <Route path="document-signing" element={<DocumentSigningPage />} />
                                  <Route path="invoices" element={<InvoiceOversightPage />} />
                                  <Route path="billing" element={<InvoiceOversightPage />} />
                                  <Route path="support-management" element={<SupportManagementPage />} />
                                  <Route path="tms-operations" element={<TMSOperationsPage />} />
                                  <Route path="order-operations" element={<OrderOperationsPage />} />
                                  <Route path="team-operations" element={<TeamOperationsPage />} />
                                  <Route path="monitoring-operations" element={<MonitoringOperationsPage />} />
                                  <Route path="inventory-operations" element={<InventoryOperationsPage />} />
                                  <Route path="billing-operations" element={<BillingOperationsPage />} />
                                  <Route path="analytics-operations" element={<AnalyticsOperationsPage />} />
                                  <Route path="manager-tools" element={<ManagerToolsPage />} />
                                </Routes>
                                                      </React.Suspense>
                                                    </MainLayout>
                                                  </ProtectedRoute>
                                                } />

                                                {/* FBA Routes */}
                        <Route path="/fba/*" element={
                          <ProtectedRoute roles={['fba', 'admin', 'selfservice', 'self_service_fba']}>
                            <React.Suspense fallback={<LoadingFallback />}>
                              <SelfServiceRoute />
                            </React.Suspense>
                          </ProtectedRoute>
                        } />

                        {/* Self Service FBA Routes */}
                        <Route path="/self-service/*" element={
                          <React.Suspense fallback={<LoadingFallback />}>
                            <SelfServiceRoute />
                          </React.Suspense>
                        } />

                        <Route path="/register" element={
                          <React.Suspense fallback={<LoadingFallback />}>
                            <SelfRegistrationPage />
                          </React.Suspense>
                        } />

                        {/* Admin Routes */}
                        <Route path="/admin/*" element={
                          <ProtectedRoute roles={['admin']}>
                            <MainLayout>
                              <React.Suspense fallback={<LoadingFallback />}>
                                <Routes>
                                  <Route index element={<AdminDashboard />} />
                                  <Route path="dashboard" element={<AdminDashboard />} />
                                  <Route path="analytics" element={<IntelligenceHubPage />} />
                                  <Route path="ai" element={<AIPage />} />
                                  <Route path="ai-dashboard" element={<Navigate to="/admin/ai?tab=1" replace />} />
                                  <Route path="ai-assistant" element={<Navigate to="/admin/ai?tab=0" replace />} />
                                                                    <Route path="orders" element={<OrderCockpitPage />} />
                                                                    <Route path="orders/:orderId" element={<OrderDetailPage />} />
                                                                    <Route path="orders-list" element={<Navigate to="/admin/orders" replace />} />
                                                                    <Route path="orders/:id/edit" element={<Navigate to="/admin/orders" replace />} />
                                                                    <Route path="orders/new" element={<Navigate to="/admin/orders" replace />} />
                                  <Route path="products" element={<ProductCockpitPage />} />
                                  <Route path="products-list" element={<Navigate to="/admin/products" replace />} />
                                  <Route path="warehouses" element={<WarehouseManagementPage />} />
                                                                    <Route path="warehouse-3d" element={<Warehouse3D />} />
                                                                    <Route path="suppliers"element={<Navigate to="/admin/logistics?tab=0" replace />} />
                                  <Route path="workforce" element={<WorkforceCockpitPage />} />
                                  <Route path="user-management" element={
                                    <React.Suspense fallback={<LoadingFallback message="Loading User Management..." />}>
                                      <UserManagementAdvancedPage />
                                    </React.Suspense>
                                  } />
                                  <Route path="users-list" element={<Navigate to="/admin/users" replace />} />
                                  <Route path="users" element={
                                    <React.Suspense fallback={<LoadingFallback message="Loading User Management..." />}>
                                      <UserManagementPage />
                                    </React.Suspense>
                                  } />
                                  <Route path="advanced-picking" element={<PickingOptimizationPage />} />
                                  <Route path="ai-generative" element={<Navigate to="/admin/ai?tab=3" replace />} />
                                  <Route path="chatbot" element={<Navigate to="/admin/ai?tab=0" replace />} />
                                  <Route path="performance" element={<PerformancePage />} />
                                  <Route path="configuration" element={<SettingsPage />} />
                                  <Route path="advanced-analytics" element={<AdvancedAnalyticsPage />} />
                                  <Route path="notifications" element={<NotificationPage />} />
                                  <Route path="reporting" element={<IntelligenceHubPage />} />
                                                                                                                                        <Route path="picking-waves" element={<PickingWavesPage />} />
                                  <Route path="picking-lists" element={<PickingListsPage />} />
                                                                                                                                        <Route path="hr-management" element={<Navigate to="/admin/workforce" replace />} />
                                  <Route path="financial-management" element={<Navigate to="/admin/financial?tab=3" replace />} />
                                                                                                                                                                          <Route path="carrier-management" element={<Navigate to="/admin/logistics?tab=1" replace />} />
                                  <Route path="ai" element={<AICockpitPage />} />
                                  <Route path="financial" element={<FinancialCockpitPage />} />
                                  <Route path="logistics" element={<LogisticsCockpitPage />} />
                                  <Route path="returns" element={<ReturnsCockpitPage />} />
                                  <Route path="returns-management" element={<Navigate to="/admin/returns" replace />} />
                                  <Route path="return-management" element={<Navigate to="/admin/returns" replace />} />
                                  <Route path="rate-shopping" element={<Navigate to="/admin/logistics?tab=2" replace />} />
                                  <Route path="carrier-performance" element={<Navigate to="/admin/logistics?tab=3" replace />} />

                                  {/* Billing Management & Analytics */}
                                  <Route path="client-pricing/:clientId" element={<Navigate to="/admin/financial?tab=1" replace />} />
                                  <Route path="invoice-management" element={<Navigate to="/admin/financial?tab=0" replace />} />
                                  <Route path="surcharge-rules" element={<Navigate to="/admin/financial?tab=2" replace />} />
                                  <Route path="executive-dashboard" element={
                                    <ProtectedRoute roles={['admin']}>
                                      <ExecutiveDashboardPage />
                                    </ProtectedRoute>
                                  } />
                                  <Route path="custom-reports" element={
                                    <ProtectedRoute roles={['admin']}>
                                      <CustomReportBuilderPage />
                                    </ProtectedRoute>
                                  } />
                                  <Route path="sales-reports" element={<IntelligenceHubPage />} />

                                  {/* Advanced Features */}
                                  <Route path="exchange-management" element={
                                    <ProtectedRoute roles={['admin']}>
                                      <ExchangeManagementPage />
                                    </ProtectedRoute>
                                  } />
                                  <Route path="marketplace-connections" element={
                                    <ProtectedRoute roles={['admin']}>
                                      <MarketplaceConnectionsPage />
                                    </ProtectedRoute>
                                  } />
                                  <Route path="user-management-advanced" element={<Navigate to="/admin/workforce" replace />} />
                                  <Route path="ai-analytics" element={<Navigate to="/admin/ai?tab=2" replace />} />
                                  <Route path="ecosystem-dashboard" element={<EcosystemDashboardPage />} />
                                  <Route path="multi-warehouse"element={<MultiWarehousePage />} />
                                  <Route path="chatbot-assistant" element={<ChatbotAssistantPage />} />
                                  <Route path="control-agents" element={
                                    <React.Suspense fallback={<LoadingFallback />}>
                                      <ControlAgentsDashboard />
                                    </React.Suspense>
                                  } />
                                  <Route path="control-agents/deadline" element={
                                    <React.Suspense fallback={<LoadingFallback />}>
                                      <DeadlineControlAgentDashboard />
                                    </React.Suspense>
                                  } />
                                  <Route path="control-agents/flow" element={
                                    <React.Suspense fallback={<LoadingFallback />}>
                                      <FlowControlAgentDashboard />
                                    </React.Suspense>
                                  } />
                                  <Route path="control-agents/inventory" element={
                                    <React.Suspense fallback={<LoadingFallback />}>
                                      <InventoryControlAgentDashboard />
                                    </React.Suspense>
                                  } />
                                  <Route path="control-agents/error" element={
                                    <React.Suspense fallback={<LoadingFallback />}>
                                      <ErrorControlAgentDashboard />
                                    </React.Suspense>
                                  } />
                                  <Route path="supplier-management" element={<Navigate to="/admin/suppliers" replace />} />
                                  <Route path="resource-planning" element={<ResourcePlanningPage />} />
                                  <Route path="marketplace" element={<MarketplacePage />} />
                                  <Route path="partner-management" element={<PartnerManagementPage />} />
                                  <Route path="product-management" element={<Navigate to="/admin/products" replace />} />
                                  <Route path="label-management" element={<LabelManagementPage />} />
                                                                                                                                        <Route path="transport-management" element={<TransportManagementPage />} />
                                                                                                                                        <Route path="transport" element={<TransportManagementPage />} />
                                                                                                                                        <Route path="ttms-trading" element={<TTMSPage />} />
                                                                    
                                                                                                                                        {/* TMS Feature Pages (Fleetbase-inspired) - Admin Access */}
                                                                                                                                        <Route path="tms/fleet" element={<FleetManagementPage />} />
                                                                                                                                        <Route path="tms/dispatch" element={<DriverDispatchPage />} />
                                                                                                                                        <Route path="tms/yard" element={<YardManagementPage />} />
                                                                                                                                        <Route path="tms/dock" element={<DockSchedulingPage />} />
                                                                                                                                        <Route path="tms/claims" element={<ClaimsManagementPage />} />
                                                                                                                                        <Route path="tms/billing" element={<FreightBillingPage />} />
                                                                                                                                        <Route path="tms/load-planning" element={<LoadPlanningPage />} />
                                                                                                                                        <Route path="tms/partners" element={<PartnerManagementPage />} />
                                                                                                                                        <Route path="tms/operations" element={<TMSOperationsPage />} />
                                                                    <Route path="forecasting-management"element={<ForecastingManagementPage />} />
                                  <Route path="delivery-slot-management" element={<DeliverySlotManagementPage />} />
                                  <Route path="analytics-advanced-management" element={<AnalyticsAdvancedManagementPage />} />
                                  <Route path="quality-control-management" element={<QualityControlManagementPage />} />
                                  <Route path="planning-management" element={<PlanningManagementPage />} />
                                  <Route path="hr-management" element={<Navigate to="/admin/workforce" replace />} />
                                  <Route path="hr/employees" element={<EmployeesListPage />} />
                                  <Route path="hr/employees/new" element={<Navigate to="/admin/workforce" replace />} />
                                  <Route path="hr/employees/:employeeId/edit" element={<Navigate to="/admin/workforce" replace />} />
                                  <Route path="products/new" element={<Navigate to="/admin/products" replace />} />
                                  <Route path="products/:productId/edit" element={<Navigate to="/admin/products" replace />} />
                                  <Route path="products/:productId/serials" element={<SerialNumbersListPage />} />
                                  <Route path="serial-numbers" element={<SerialNumbersListPage />} />
                                  <Route path="customers/new" element={<Navigate to="/admin/customers" replace />} />
                                  <Route path="customers/:customerId/edit" element={<Navigate to="/admin/customers" replace />} />
                                  <Route path="billing-management" element={<Navigate to="/admin/financial?tab=3" replace />} />
                                  <Route path="inventory-management" element={<InventoryManagementPage />} />
                                  <Route path="order-management" element={<Navigate to="/admin/orders" replace />} />
                                  <Route path="warehouse-management" element={<Navigate to="/admin/warehouses" replace />} />
                                  <Route path="shipping" element={<ShippingManagementPage />} />
                                  <Route path="shipping-management" element={<Navigate to="/admin/shipping" replace />} />
                                  <Route path="picking-management" element={<PickingManagementPage />} />
                                                                    <Route path="workstation-monitoring" element={<WorkstationMonitoringPage />} />
                                                                    <Route path="mesh-network" element={<MeshNetworkStatusPage />} />
                                                                    <Route path="ai-actions-dashboard" element={<AIActionsDashboardPage />} />
                                  <Route path="permissions" element={<PermissionsPage />} />

                                  <Route path="configuration" element={<ConfigurationPage />} />
                                  <Route path="customers" element={<CustomerCockpitPage />} />
                                  <Route path="picking-visualization" element={<PickingVisualizationPage />} />
                                  <Route path="role-management" element={<RoleManagementPage />} />
                                  <Route path="security-demo" element={<SecurityDemoPage />} />

                                  <Route path="chatbot-analytics" element={<Navigate to="/admin/ai?tab=2" replace />} />
                                  <Route path="agent-inbox" element={<Navigate to="/admin/ai?tab=1" replace />} />
                                  <Route path="omnichannel" element={<OmnichannelDashboard />} />

                                  {/* Vibecraft-inspired AI Visualization Features */}
                                  <Route path="vibecraft-tools" element={
                                    <React.Suspense fallback={<LoadingFallback message="Loading AI Tool Visualization..." />}>
                                      <AIToolVisualizationDashboard />
                                    </React.Suspense>
                                  } />
                                  <Route path="vibecraft-workstations" element={
                                    <React.Suspense fallback={<LoadingFallback message="Loading Workstation Orchestration..." />}>
                                      <WorkstationOrchestrationView />
                                    </React.Suspense>
                                  } />
                                  <Route path="vibecraft-activity" element={
                                    <React.Suspense fallback={<LoadingFallback message="Loading Activity Feed..." />}>
                                      <ActivityFeedPanel />
                                    </React.Suspense>
                                  } />
                                  <Route path="vibecraft-monitor" element={
                                    <React.Suspense fallback={<LoadingFallback message="Loading AI Agent Monitor 3D..." />}>
                                      <AIAgentMonitor3D />
                                    </React.Suspense>
                                  } />

                                  {/* Supermemory-inspired Memory Graph */}
                                  <Route path="memory-graph" element={
                                    <React.Suspense fallback={<LoadingFallback message="Loading Memory Graph..." />}>
                                      <MemoryGraph />
                                    </React.Suspense>
                                  } />

                                  <Route path="settings" element={<SettingsPage />} />
                                  <Route path="system-settings" element={<SystemSettingsPage />} />
                                  <Route path="audit-logs" element={<AuditLogsPage />} />
                                  <Route path="reports" element={<IntelligenceHubPage />} />
                                  <Route path="inventory" element={<AdminInventoryPage />} />
                                  <Route path="search" element={<SearchResultsPage />} />

                                  {/* Wired-up pages - previously orphaned */}
                                  <Route path="warehouse-layout-editor" element={<WarehouseLayoutEditorPage />} />
                                  <Route path="warehouse-layout" element={<WarehouseLayoutEditor />} />
                                  <Route path="warehouses-list" element={<WarehousesListPage />} />
                                  <Route path="chatbot-management" element={<ChatbotPage />} />
                                  <Route path="reporting-dashboard" element={<ReportingPage />} />
                                  <Route path="quality-control" element={<QualityControlPage />} />
                                  <Route path="picking-routes" element={<PickingRoutesPage />} />
                                  <Route path="dashboard-widgets" element={<DashboardWidgetsPage />} />
                                  <Route path="ui-enhancer" element={<UIEnhancerPage />} />
                                  <Route path="live-monitoring" element={<LiveMonitoringPage />} />
                                  <Route path="performance-booster" element={<PerformanceBoosterPage />} />
                                  <Route path="ux-enhancer" element={<UXEnhancerPage />} />

                                  {/* ERPNext Gap Fixes */}
                                  <Route path="putaway-rules" element={<PutawayRulesPage />} />
                                  <Route path="stock-reservations" element={<StockReservationsPage />} />
                                  <Route path="automatic-reorder" element={<AutomaticReorderPage />} />
                                  <Route path="non-conformance" element={<NonConformanceManagementPage />} />
                                  <Route path="stock-ageing" element={<StockAgeingReportsPage />} />

                                  {/* Wired-up aggregate pages */}
                                  <Route path="support-management" element={<SupportManagementPage />} />
                                  <Route path="tms-operations" element={<TMSOperationsPage />} />
                                  <Route path="order-operations" element={<OrderOperationsPage />} />
                                  <Route path="team-operations" element={<TeamOperationsPage />} />
                                  <Route path="monitoring-operations" element={<MonitoringOperationsPage />} />
                                  <Route path="inventory-operations" element={<InventoryOperationsPage />} />
                                  <Route path="billing-operations" element={<BillingOperationsPage />} />
                                  <Route path="analytics-operations" element={<AnalyticsOperationsPage />} />
                                  <Route path="marketplace-operations" element={<MarketplaceOperationsPage />} />
                                  <Route path="warehouse-3d-tools" element={<Warehouse3DToolsPage />} />
                                  <Route path="product-operations" element={<ProductOperationsPage />} />
                                  <Route path="manager-tools" element={<ManagerToolsPage />} />
                                  <Route path="b2b-operations" element={<B2BOperationsPage />} />

                                  {/* SpacetimeDB Monitoring Dashboard */}
                                                                                                                                                                                                    <Route path="monitoring-dashboard" element={
                                                                                                                                                                                                      <React.Suspense fallback={<LoadingFallback message="Loading Monitoring Dashboard..." />}>
                                                                                                                                                                                                        <MonitoringDashboard />
                                                                                                                                                                                                      </React.Suspense>
                                                                                                                                                                                                    } />
                                                                                                  
                                                                                                                                                                                                    {/* Utility Pattern Monitoring Dashboard (Fleetbase-inspired) */}
                                                                                                                                                                                                    <Route path="utility-monitoring" element={
                                                                                                                                                                                                      <React.Suspense fallback={<LoadingFallback message="Loading Utility Monitoring..." />}>
                                                                                                                                                                                                        <UtilityMonitoringDashboard />
                                                                                                                                                                                                      </React.Suspense>
                                                                                                                                                                                                    } />
                                                                                                                                </Routes>
                              </React.Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        } />

                        {/* Test Routes */}
                        <Route path="/test/*" element={
                          <ProtectedRoute roles={['admin']}>
                            <MainLayout>
                              <React.Suspense fallback={<LoadingFallback />}>
                                <Routes>
                                  <Route path="frappe-crm-demo" element={<FrappeCRMComponentsDemo />} />
                                </Routes>
                              </React.Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        } />

                        {/* Client Routes */}
                        <Route path="/client/*" element={
                          <ProtectedRoute roles={['client', 'viewer']}>
                            <MainLayout>
                              <React.Suspense fallback={<LoadingFallback />}>
                                <Routes>
                                  <Route index element={<ClientDashboard />} />
                                  <Route path="dashboard" element={<ClientDashboard />} />
                                  <Route path="search" element={<SearchResultsPage />} />
                                  <Route path="orders" element={<OrdersPage />} />
                                  <Route path="oms" element={<OMSPage />} />
                                  <Route path="oms-dashboard" element={<OMSPage />} />
                                  <Route path="cms-integrations" element={<CMSIntegrationsPage />} />
                                  <Route path="parcel-sorting" element={<ParcelSortingPage />} />
                                  <Route path="expeditions" element={<TMSShipmentPage />} />
                                                                    <Route path="tms" element={<TMSPage />} />
                                                                    <Route path="ttms-trading" element={<TTMSPage />} />
                                  
                                                                    {/* TMS Feature Pages (Fleetbase-inspired) */}
                                                                    <Route path="tms/fleet" element={<FleetManagementPage />} />
                                                                    <Route path="tms/dispatch" element={<DriverDispatchPage />} />
                                                                    <Route path="tms/yard" element={<YardManagementPage />} />
                                                                    <Route path="tms/dock" element={<DockSchedulingPage />} />
                                                                    <Route path="tms/claims" element={<ClaimsManagementPage />} />
                                                                    <Route path="tms/billing" element={<FreightBillingPage />} />
                                                                    <Route path="tms/load-planning" element={<LoadPlanningPage />} />
                                                                    <Route path="tms/partners" element={<PartnerManagementPage />} />
                                                                    <Route path="products"element={<ProductOMSPage />} />
                                                                                                                                        <Route path="inventory" element={<ClientInventoryPage />} />
                                                                                                      <Route path="marketplace" element={<OMSPage />} />
                                                                                                      <Route path="support-modern" element={<SupportPage />} />

                                  <Route path="reports" element={<ReportsPage />} />
                                  <Route path="profile" element={<ProfilePage />} />
                                  <Route path="orders/:orderId" element={<OrderDetailPage />} />
                                  <Route path="orders/:orderId/create-rma" element={<CreateRMAPage />} />
                                  <Route path="rma/:rmaNumber" element={<RMADetailsPage />} />

                                  <Route path="workflows" element={<WorkflowsPage />} />
                                  <Route path="billing-estimate" element={<BillingEstimatePage />} />
                                  <Route path="billing" element={<BillingDashboardClientPage />} />
                                  <Route path="portal/invoices" element={<InvoicePortalPage />} />
                                  <Route path="portal/invoices/:invoiceId" element={<InvoiceDetailPortalPage />} />

                                </Routes>
                              </React.Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        } />

                        {/* NO catch-all route - let nginx handle unmatched routes including API docs */}
                      </Routes>
                    </PageTransition>
                  </RoleProvider>
                </AuthProvider>
              </Router>
            </ErrorBoundaryWithAPI>
          </SnackbarProvider>
        </AdvancedThemeProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeModeProvider>
        <AppContent />
      </ThemeModeProvider>
    </ErrorBoundary>
  );
};

export default React.memo(App);

